import {PropsWithChildren, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useGuestGroup} from 'hooks';
import {Crown} from 'lucide-react';
import {EmptyGuestItem, GuestItem} from './components/GuestItem';
import {PanelsGroupWrapper, PanelsGroupContainer} from './styled';

const PanelGroupTitle = ({children}: PropsWithChildren) => {
  return (
    <div
      className={`text-sm font-semibold leading-6 mb-3 flex gap-2 items-center text-[var(--font-secondary)] uppercase`}
    >
      {children}
    </div>
  );
};

type Props = {
  handleRegisterNewGuest?: (isLeader?: boolean) => void;
};

function GuestsPanel({handleRegisterNewGuest}: Props) {
  const {t} = useTranslation();
  const {guestGroup, areAllGuestsRegistered, missingGuests} = useGuestGroup();

  const knownNumberOfGuests = guestGroup?.known_number_of_guests ?? 0;
  const showAccompanyingGuests = knownNumberOfGuests > 1;
  const members = guestGroup?.members;

  const accompanyingMembers = members?.filter(
    member => member.id !== guestGroup?.leader_id,
  );

  const leaderGuest = members?.find(member => member.id === guestGroup?.leader_id);

  const missingGuestArray = useMemo(() => {
    if (!guestGroup || areAllGuestsRegistered) {
      return [];
    }
    const totalUnregisteredGuests = !leaderGuest ? missingGuests - 1 : missingGuests;
    return new Array(totalUnregisteredGuests).fill(null);
  }, [guestGroup, areAllGuestsRegistered, missingGuests, leaderGuest]);

  return (
    <>
      <PanelsGroupWrapper>
        <PanelGroupTitle>
          {t('guest_in_charge')}
          <Crown
            size="20"
            color="#FFD166"
            fill="#FFD166"
            className="top-[-1px] relative"
          />
        </PanelGroupTitle>
        <PanelsGroupContainer>
          {leaderGuest ? (
            <GuestItem guestMember={{...leaderGuest, isLeader: true}} />
          ) : (
            <EmptyGuestItem onClick={() => handleRegisterNewGuest?.(true)} />
          )}
        </PanelsGroupContainer>
      </PanelsGroupWrapper>
      <PanelsGroupWrapper>
        {showAccompanyingGuests && (
          <PanelGroupTitle>{t('accompanying_guests')}</PanelGroupTitle>
        )}
        <PanelsGroupContainer>
          {accompanyingMembers?.map((guestMember, index: number) => {
            return <GuestItem key={index} guestMember={guestMember} />;
          })}
          {missingGuestArray.map((_, index) => (
            <EmptyGuestItem key={index} onClick={() => handleRegisterNewGuest?.(false)} />
          ))}
        </PanelsGroupContainer>
      </PanelsGroupWrapper>
    </>
  );
}

export {GuestsPanel};
